import { IconChevronDown, IconClipboardCheck, IconTable } from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ModuleHeader } from "~/components/ModuleHeader";
import { StyledMenu } from "~/components/StyledMenu";
import type { DebugPayload } from "~/shared/ai/assistant-event-contract";
import { Button, Drawer, Flex, showNotification, Text, ThemeIcon, useMediaQuery } from "~/ui";
import type { ActiveReportArtifact } from "~/utils/types/assistant-report-artifact";
import styles from "./AssistantArtifactPanel.module.css";
import { AssistantDebugModal } from "./AssistantDebugModal";
import { AssistantResults, type AssistantResultsHandle } from "./AssistantResults";
import { reportCoverageLine } from "./query-methodology-view-model";
import type { WorkspaceDisposition } from "./report-artifact-state";
import { reportCommitRejectionCopy, visibleDisposition } from "./report-artifact-state";
import { reportArtifactVersionKey } from "./report-column-state";
import { formatCalendarDay } from "./report-field-formatters";
import { TruncatingTitle } from "./TruncatingTitle";

interface AssistantArtifactPanelProps {
  opened: boolean;
  onClose: () => void;
  artifact: ActiveReportArtifact | null;
  activeArtifactKey: string | null;
  onShowLatest: () => void;
  disposition: WorkspaceDisposition | null;
  commitRejected: boolean;
  queryError: string | null;
  debugPayloads: DebugPayload[];
  onClearDebug: () => void;
  onOpenMethodology: () => void;
}

export const shouldMountAssistantArtifactPanel = (hasOpened: boolean, opened: boolean): boolean => hasOpened || opened;

export const panelArtifactForRender = (
  opened: boolean,
  artifact: ActiveReportArtifact | null,
  mountedArtifact: ActiveReportArtifact | null,
): ActiveReportArtifact | null => (opened ? artifact : mountedArtifact);

export const hasNewerAssistantArtifact = (displayedArtifact: ActiveReportArtifact | null, activeArtifactKey: string | null): boolean =>
  displayedArtifact !== null && activeArtifactKey !== null && reportArtifactVersionKey(displayedArtifact) !== activeArtifactKey;

export const AssistantArtifactPanelTitle = ({ title }: { title: string }) => (
  <Flex align="center" gap="sm" style={{ minWidth: 0 }}>
    <ThemeIcon size={28} variant="light" color="blue" radius="md">
      <IconTable size={16} />
    </ThemeIcon>
    {/* This drawer already has a definite title width; use the shared idiom for consistency. */}
    <TruncatingTitle title={title} size="lg" />
  </Flex>
);

export const AssistantArtifactPanel = ({
  opened,
  onClose,
  artifact,
  activeArtifactKey,
  onShowLatest,
  disposition,
  commitRejected,
  queryError,
  debugPayloads,
  onClearDebug,
  onOpenMethodology,
}: AssistantArtifactPanelProps) => {
  const isNarrow = useMediaQuery("(max-width: 1023px)");
  const [hasOpened, setHasOpened] = useState(opened);
  const [mountedArtifact, setMountedArtifact] = useState<ActiveReportArtifact | null>(opened ? artifact : null);
  const resultsRef = useRef<AssistantResultsHandle>(null);

  useEffect(() => {
    if (opened) {
      setHasOpened(true);
      setMountedArtifact(artifact);
    }
  }, [artifact, opened]);
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async (format: "xlsx" | "csv") => {
    setExporting(true);
    try {
      await resultsRef.current?.download(format);
    } catch {
      showNotification({
        color: "red",
        title: "Export failed",
        message:
          format === "xlsx"
            ? "The Excel file could not be built. Try again, or choose CSV."
            : "The CSV file could not be built. Try again, or choose Excel.",
      });
    } finally {
      setExporting(false);
    }
  }, []);
  const displayedArtifact = panelArtifactForRender(opened, artifact, mountedArtifact);
  const hasNewerArtifact = hasNewerAssistantArtifact(displayedArtifact, activeArtifactKey);
  const shouldMount = shouldMountAssistantArtifactPanel(hasOpened, opened);
  return (
    <Drawer
      position="right"
      size={isNarrow ? "100vw" : "66vw"}
      opened={opened}
      onClose={onClose}
      keepMounted
      title={<AssistantArtifactPanelTitle title={displayedArtifact?.presentation.title ?? "Results"} />}
      titleClassName={styles.title}
      bodyClassName={styles.body}
      headerClassName={styles.header}
    >
      {shouldMount && (
        <Flex direction="column" h="100%" gap="sm" style={{ minWidth: 0 }}>
          <Flex align="center" justify="space-between" gap="sm" wrap="wrap">
            <Flex align="center" gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
              {displayedArtifact && (
                <Text size="sm" c="gray.6">
                  {reportCoverageLine(
                    displayedArtifact.dataset.methodology.coverage,
                    displayedArtifact.dataset.presentationManifest?.coverageCopy,
                    displayedArtifact.dataset.methodology,
                  )}
                </Text>
              )}
              {displayedArtifact?.dataset.freshness?.asOf && (
                <Text size="xs" c="gray.6">
                  As of {formatCalendarDay(displayedArtifact.dataset.freshness.asOf) || displayedArtifact.dataset.freshness.asOf}
                </Text>
              )}
            </Flex>
            <Flex align="center" gap="xs" wrap="wrap">
              {/* Visual weight follows the product thesis: verification outranks export,
                  and the superadmin debug affordance trails both real actions. */}
              {(displayedArtifact || queryError) && (
                <Button variant="light" size="sm" leftIcon={<IconClipboardCheck size={16} />} onClick={onOpenMethodology}>
                  Check My Work
                </Button>
              )}
              {displayedArtifact && (
                <StyledMenu
                  withinPortal
                  dropdownZIndex={300}
                  button={
                    <ModuleHeader.Export
                      rightIcon={<IconChevronDown size={16} />}
                      exportIsLoading={exporting}
                      variant="subtle"
                      color="gray"
                      size="sm"
                      disabled={!displayedArtifact.dataset.rows.length}
                    />
                  }
                >
                  <StyledMenu.Item onClick={() => void handleExport("xlsx")} disabled={exporting}>
                    Excel (.xlsx)
                  </StyledMenu.Item>
                  <StyledMenu.Item onClick={() => void handleExport("csv")} disabled={exporting}>
                    CSV (.csv)
                  </StyledMenu.Item>
                </StyledMenu>
              )}
              <AssistantDebugModal debugPayloads={debugPayloads} onClear={onClearDebug} />
            </Flex>
          </Flex>
          {hasNewerArtifact && (
            <Button size="xs" variant="light" onClick={onShowLatest} style={{ alignSelf: "flex-start" }}>
              Newer report available · Show latest
            </Button>
          )}
          {visibleDisposition(disposition) && (
            <Text size="sm" c="gray.6">
              {visibleDisposition(disposition)}
            </Text>
          )}
          {commitRejected && (
            <Text size="sm" c="orange.8">
              {reportCommitRejectionCopy(displayedArtifact !== null)}
            </Text>
          )}
          <AssistantResults ref={resultsRef} artifact={displayedArtifact} visible={opened} />
        </Flex>
      )}
    </Drawer>
  );
};
